import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UsersThree, EnvelopeSimple, Trash, Copy, ArrowClockwise, Link as LinkIcon, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShareDialog({ patient, open, onOpenChange }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [inviting, setInviting] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // { invite_code, invitee_email, role, status }

  const inviteUrl = (code) => `${window.location.origin}/accept/${code}`;

  const load = async () => {
    if (!patient) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patient.id}/shares`);
      setShares(data);
    } catch (e) {
      // owner-only; if 404 we're not owner
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, patient?.id]);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/shares`, { email, role });
      setJustCreated(data);
      // auto-copy the link
      try { await navigator.clipboard.writeText(inviteUrl(data.invite_code)); } catch {}
      toast.success("Invite link copied — paste it wherever you like");
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally { setInviting(false); }
  };

  const changeRole = async (s, newRole) => {
    await api.patch(`/patients/${patient.id}/shares/${s.id}`, { role: newRole });
    toast.success("Role updated");
    load();
  };
  const revoke = async (s) => {
    if (!window.confirm(`Remove ${s.invitee_email} from ${patient.name}?`)) return;
    await api.delete(`/patients/${patient.id}/shares/${s.id}`);
    toast.success("Removed");
    load();
  };
  const copyLink = async (s) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(s.invite_code));
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — link: " + inviteUrl(s.invite_code));
    }
  };

  if (!patient) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UsersThree size={22} weight="duotone" className="text-[#2C5545]" />
            Share {patient.name}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#576A62]">
            Invite family or trusted friends to co-manage this patient's care.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-[#576A62]">
          Editors can add and edit records; viewers can only view. After inviting, copy the link and send it to them however you like (text, email, chat).
        </p>

        <form onSubmit={invite} className="flex gap-2 items-end">
          <label className="flex-1 block">
            <div className="text-xs font-medium mb-1.5 uppercase tracking-widest text-[#576A62]">Email</div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="family@example.com"
              data-testid="share-invite-email"
              className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-4 focus:ring-2 focus:ring-[#2C5545] outline-none"
            />
          </label>
          <label className="block w-32">
            <div className="text-xs font-medium mb-1.5 uppercase tracking-widest text-[#576A62]">Role</div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="share-invite-role"
              className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-3"
            >
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={inviting}
            data-testid="share-invite-submit"
            className="h-11 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white font-semibold px-5 disabled:opacity-60"
          >
            {inviting ? "Creating…" : "Create invite"}
          </button>
        </form>

        {justCreated && justCreated.status === "pending" && (
          <div className="rounded-xl bg-[#3E7B5C]/10 border border-[#3E7B5C]/30 p-4" data-testid="share-just-created">
            <div className="text-xs uppercase tracking-widest text-[#3E7B5C] font-semibold flex items-center gap-1">
              <Check weight="bold" size={14} /> Invite link ready
            </div>
            <div className="mt-2 text-sm text-[#1A2520]">
              Share this link with <span className="font-semibold">{justCreated.invitee_email}</span> — they can sign up or sign in to accept.
            </div>
            <div className="mt-3 flex gap-2 items-center">
              <input
                readOnly
                value={inviteUrl(justCreated.invite_code)}
                data-testid="share-just-created-url"
                onFocus={(e) => e.target.select()}
                className="flex-1 h-10 rounded-full border border-[#EBE8E0] bg-white px-4 text-sm font-mono"
              />
              <button
                onClick={() => copyLink(justCreated)}
                data-testid="share-just-created-copy"
                className="rounded-full bg-[#2C5545] hover:bg-[#224235] text-white text-sm font-semibold px-4 h-10 inline-flex items-center gap-1 shrink-0"
              >
                <Copy size={14} weight="bold" /> Copy
              </button>
            </div>
          </div>
        )}
        {justCreated && justCreated.status === "accepted" && (
          <div className="rounded-xl bg-[#3E7B5C]/10 border border-[#3E7B5C]/30 p-4 text-sm" data-testid="share-just-accepted">
            <span className="font-semibold text-[#3E7B5C]">✓ Auto-accepted.</span> <span className="text-[#576A62]">{justCreated.invitee_email} already has a EverCara account and now has access.</span>
          </div>
        )}

        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#576A62] mb-2">People with access</div>
          {loading ? (
            <div className="space-y-2">{[0,1].map((i) => <div key={i} className="h-14 rounded-xl bg-[#EBE8E0]/50 animate-pulse" />)}</div>
          ) : shares.length === 0 ? (
            <div className="text-sm text-[#576A62] py-4">No collaborators yet.</div>
          ) : (
            <div className="space-y-2">
              {shares.map((s) => (
                <div key={s.id} data-testid={`share-row-${s.id}`} className="rounded-xl border border-[#EBE8E0] p-3 flex items-center gap-3">
                  <EnvelopeSimple size={22} weight="duotone" className="text-[#2C5545] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate" data-testid={`share-email-${s.id}`}>{s.invitee_email}</div>
                    <div className="text-xs text-[#576A62] mt-0.5">
                      <span className={`inline-block rounded-full px-2 py-0.5 mr-2 ${s.status === "accepted" ? "bg-[#3E7B5C]/15 text-[#3E7B5C]" : s.status === "pending" ? "bg-[#D49A4C]/15 text-[#B37927]" : "bg-[#EBE8E0] text-[#576A62]"}`}>{s.status}</span>
                    </div>
                  </div>
                  <select
                    value={s.role}
                    onChange={(e) => changeRole(s, e.target.value)}
                    data-testid={`share-role-${s.id}`}
                    className="h-9 rounded-full border border-[#EBE8E0] bg-white px-3 text-sm"
                  >
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  {s.status === "pending" && (
                    <button
                      onClick={() => copyLink(s)}
                      title="Copy invite link"
                      data-testid={`share-copy-${s.id}`}
                      className="p-2 rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0]/60"
                    ><LinkIcon size={16} /></button>
                  )}
                  <button
                    onClick={() => revoke(s)}
                    data-testid={`share-revoke-${s.id}`}
                    className="p-2 rounded-full border border-[#EBE8E0] text-[#C86A51] hover:bg-[#C86A51]/10"
                  ><Trash size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            data-testid="share-close"
            className="rounded-full px-5 py-2.5 border border-[#EBE8E0] text-[#576A62] hover:bg-[#EBE8E0]/60"
          >Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
